// THE TempDash brand: one place for the symbol and the wordmark (founder
// 2026-10-02 rebrand). Every app, web page, email and generated icon reads
// from here, so the logo can never drift into four copies again.
//
// ⭐ SWITCHING MARKS. The founder shortlisted two symbols and wants to move
// between them. Change ACTIVE_MARK, then regenerate every PNG:
//   node scripts/brand-assets.mjs
// and rebuild the apps (icons are native). In-app and web drawings follow the
// constant on their own.
//
// The symbol carries the colour and reads on white, on navy and in Gmail's dark
// mode. The wordmark is plain lowercase "tempdash" in Onest Bold: ink on a
// light surface, white on a dark one (Monzo's rule). Where we cannot know the
// background (email), the wordmark takes WORDMARK.email, a mid tone that holds
// on both.
//
// Dependency free (shared/ rule): plain SVG strings. React Native draws them
// with react-native-svg SvgXml, the web inlines them, the asset script rasterises
// them with sharp.

export const ACTIVE_MARK = 'g2'

export const MARKS = {
  // G2 "Two dashes": two leaning rounded pills stacked. Shifts, and speed.
  g2: {
    name: 'Two dashes',
    viewBox: '0 0 100 100',
    body:
      '<defs>' +
      '<linearGradient id="tdG2a" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#3EE6E8"/><stop offset="1" stop-color="#1C7A9C"/></linearGradient>' +
      '<linearGradient id="tdG2b" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#A9A3FF"/><stop offset="1" stop-color="#218FB7"/></linearGradient>' +
      '</defs>' +
      '<g transform="skewX(-14) translate(12 0)">' +
      '<rect x="8" y="10" width="74" height="42" rx="21" fill="url(#tdG2a)"/>' +
      '<rect x="16" y="46" width="74" height="42" rx="21" fill="url(#tdG2b)" fill-opacity="0.88"/>' +
      '</g>',
    // One flat colour (notification + monochrome icons): the overlap would melt
    // the two pills into one blob, so they part with a clear gap instead.
    mono:
      '<g transform="skewX(-14) translate(12 0)">' +
      '<rect x="8" y="8" width="74" height="38" rx="19"/>' +
      '<rect x="16" y="54" width="74" height="38" rx="19"/>' +
      '</g>',
  },
  // C1 "Chunky td": the fallback.
  c1: {
    name: 'Chunky td',
    viewBox: '0 0 100 100',
    body:
      '<rect x="12" y="8" width="22" height="84" rx="11" fill="#00CED1"/>' +
      '<rect x="2" y="32" width="50" height="20" rx="10" fill="#7FE7E9" fill-opacity="0.85"/>' +
      '<path fill="#0FA3B1" fill-opacity="0.85" fill-rule="evenodd" d="M64 36a27 27 0 1 1 0 54a27 27 0 1 1 0-54zm0 15a12 12 0 1 0 0 24a12 12 0 1 0 0-24z"/>' +
      '<rect x="77" y="8" width="20" height="84" rx="10" fill="#0B7C8C" fill-opacity="0.9"/>',
  },
}

// The full SVG for a mark. `fill` paints it one flat colour (a white silhouette
// for Android notification and monochrome icons), using the mark's `mono` cut
// when it has one.
export function markSvg({ mark = ACTIVE_MARK, size = 100, fill = null } = {}) {
  const m = MARKS[mark] || MARKS[ACTIVE_MARK]
  let body = m.body
  if (fill) {
    body = m.mono
      ? `<g fill="${fill}">${m.mono}</g>`
      : body.replace(/fill="[^"]*"/g, `fill="${fill}"`).replace(/ fill-opacity="[^"]*"/g, '')
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="${m.viewBox}">${body}</svg>`
}

export const WORDMARK = {
  text: 'tempdash',
  font: 'Onest',
  weight: '700',
  // Tracking as a fraction of the font size (-0.8px at 24px).
  tracking: -0.033,
  ink: '#14213D',     // on light surfaces
  onDark: '#FFFFFF',  // on dark surfaces
  email: '#1C7A9C',   // background unknown: 4.9:1 on white, 3.3:1 on Gmail's dark
  // The symbol sits this many font sizes wide beside the word, this far apart.
  markScale: 1.45,
  gap: 0.35,
}
